import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: string;
    options: string[];
}


const FillBlanksSeasons: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'FillBlanksSeasons.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/FillBlanksSeasons.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "Seasons – Choose the Correct Answer Worksheet for UKG | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: string) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Practice seasons for UKG kids with this choose the correct answer worksheet. Learn about summer, winter, rainy and spring seasons through simple MCQs." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/seasons/choose-correct" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Seasons – Choose the Correct Answer</h1>

                                    <p>
                                        Help your child understand the different seasons with this <strong>UKG multiple-choice worksheet</strong>. This activity helps children recognize and differentiate between seasons such as <strong>summer, winter, rainy,</strong> and <strong>spring</strong> in a fun and interactive way.
                                    </p>

                                    <h2>🎯 What Will Kids Learn?</h2>
                                    <ul>
                                        <li>Names and characteristics of seasons</li>
                                        <li>Appropriate clothing and weather for each season</li>
                                        <li>Seasonal changes in nature and environment</li>
                                        <li>Basic comprehension and observation skills</li>
                                    </ul>

                                    <h2>📝 Worksheet Activity</h2>
                                    <p>
                                        Children are asked to <strong>choose the correct answer</strong> from three or four simple options related to seasonal facts and visuals.
                                    </p>

                                    <h2>📘 Sample Questions</h2>
                                    <ul>
                                        <li>Which season do we use umbrellas in?<br />A) Summer B) Rainy C) Winter<br /><strong>Correct Answer:</strong> Rainy</li>
                                        <li>In which season do we wear woolen clothes?<br />A) Summer B) Winter C) Rainy<br /><strong>Correct Answer:</strong> Winter</li>
                                        <li>When do flowers bloom?<br />A) Spring B) Winter C) Monsoon<br /><strong>Correct Answer:</strong> Spring</li>
                                    </ul>

                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Benefits of This Worksheet</h2>
                                    <ul>
                                        <li>Improves seasonal awareness</li>
                                        <li>Builds vocabulary related to nature and weather</li>
                                        <li>Encourages visual observation and logical thinking</li>
                                        <li>Supports foundational general knowledge for UKG</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Seasons – Choose the Correct Answer</h1>

                                <p>
                                    Help your child understand the different seasons with this <strong>UKG multiple-choice worksheet</strong>. This activity helps children recognize and differentiate between seasons such as <strong>summer, winter, rainy,</strong> and <strong>spring</strong> in a fun and interactive way.
                                </p>

                                <h2>🎯 What Will Kids Learn?</h2>
                                <ul>
                                    <li>Names and characteristics of seasons</li>
                                    <li>Appropriate clothing and weather for each season</li>
                                    <li>Seasonal changes in nature and environment</li>
                                    <li>Basic comprehension and observation skills</li>
                                </ul>

                                <h2>📝 Worksheet Activity</h2>
                                <p>
                                    Children are asked to <strong>choose the correct answer</strong> from three or four simple options related to seasonal facts and visuals.
                                </p>

                                <h2>📘 Sample Questions</h2>
                                <ul>
                                    <li>Which season do we use umbrellas in?<br />A) Summer B) Rainy C) Winter<br /><strong>Correct Answer:</strong> Rainy</li>
                                    <li>In which season do we wear woolen clothes?<br />A) Summer B) Winter C) Rainy<br /><strong>Correct Answer:</strong> Winter</li>
                                    <li>When do flowers bloom?<br />A) Spring B) Winter C) Monsoon<br /><strong>Correct Answer:</strong> Spring</li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Benefits of This Worksheet</h2>
                                <ul>
                                    <li>Improves seasonal awareness</li>
                                    <li>Builds vocabulary related to nature and weather</li>
                                    <li>Encourages visual observation and logical thinking</li>
                                    <li>Supports foundational general knowledge for UKG</li>
                                </ul>
                            </article>
                            <div className="mt-4 mb-4">
                                <h3 className="mb-5">
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>
                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value)}>
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem key={index}
                                        className={`grid-item ${selectedOption === option
                                            ? option === questions[currentIndex]?.correct
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}>
                                        <IonRadio slot="start" value={option} />
                                        <IonLabel>{option}</IonLabel>
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default FillBlanksSeasons;
